import type { SavedTrip } from './SavedTripCard'

export type SavedTripFilterKey = 'creator' | 'vtr' | 'codebeamer'

export const SAVED_TRIP_FILTER_KEYS: SavedTripFilterKey[] = [
  'creator',
  'vtr',
  'codebeamer',
]

export type SavedTripFilterOption = { value: string; label: string }

export type SavedTripFilters = {
  values: Record<SavedTripFilterKey, string[]>
  /** Filters with a selection, in the order they were first applied. */
  activeOrder: SavedTripFilterKey[]
}

export const EMPTY_SAVED_TRIP_FILTERS: SavedTripFilters = {
  values: { creator: [], vtr: [], codebeamer: [] },
  activeOrder: [],
}

type CreatorNameContext = {
  currentUserId?: string
  currentUserDisplayName?: string
  /** Map of userId → display name for non-current users */
  creatorNames?: Record<string, string>
}

/** Display name of a route's creator, or null for system-created or unresolved creators. */
export const getCreatorName = (
  createdById: string,
  { currentUserId, currentUserDisplayName, creatorNames }: CreatorNameContext,
): string | null => {
  if (currentUserId && createdById === currentUserId && currentUserDisplayName)
    return currentUserDisplayName
  if (!createdById || createdById === 'system') return null
  return creatorNames?.[createdById] ?? null
}

const toSortedOptions = (
  entries: Map<string, string>,
): SavedTripFilterOption[] =>
  [...entries]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))

/** Options of each filter, built from the values present in the given routes. */
export const buildSavedTripFilterOptions = (
  trips: SavedTrip[],
  creatorContext: CreatorNameContext,
): Record<SavedTripFilterKey, SavedTripFilterOption[]> => {
  const creators = new Map<string, string>()
  const vtrs = new Map<string, string>()
  const codebeamerIds = new Map<string, string>()
  for (const trip of trips) {
    const creatorName = getCreatorName(trip.createdById, creatorContext)
    if (creatorName) creators.set(trip.createdById, creatorName)
    for (const { vtr, codebeamerIds: ids } of trip.assignedVtrs) {
      if (vtr) vtrs.set(vtr, vtr)
      for (const id of ids) if (id) codebeamerIds.set(id, id)
    }
  }
  return {
    creator: toSortedOptions(creators),
    vtr: toSortedOptions(vtrs),
    codebeamer: toSortedOptions(codebeamerIds),
  }
}

/**
 * A route matches when it matches every filter with a selection, and it matches
 * a filter when it has any of that filter's selected values.
 */
export const matchesSavedTripFilters = (
  trip: SavedTrip,
  { values }: SavedTripFilters,
): boolean => {
  if (values.creator.length > 0 && !values.creator.includes(trip.createdById))
    return false
  if (
    values.vtr.length > 0 &&
    !trip.assignedVtrs.some(({ vtr }) => values.vtr.includes(vtr))
  )
    return false
  if (
    values.codebeamer.length > 0 &&
    !trip.assignedVtrs.some(({ codebeamerIds }) =>
      codebeamerIds.some(id => values.codebeamer.includes(id)),
    )
  )
    return false
  return true
}

export const hasActiveSavedTripFilters = ({ activeOrder }: SavedTripFilters) =>
  activeOrder.length > 0

export const setSavedTripFilterValues = (
  filters: SavedTripFilters,
  key: SavedTripFilterKey,
  selected: string[],
): SavedTripFilters => {
  const withoutKey = filters.activeOrder.filter(k => k !== key)
  return {
    values: { ...filters.values, [key]: selected },
    activeOrder:
      selected.length === 0
        ? withoutKey
        : filters.activeOrder.includes(key)
          ? filters.activeOrder
          : [...withoutKey, key],
  }
}

/** Applied filters first, in the order they were applied, then the rest. */
export const orderSavedTripFilterKeys = ({
  activeOrder,
}: SavedTripFilters): SavedTripFilterKey[] => [
  ...activeOrder,
  ...SAVED_TRIP_FILTER_KEYS.filter(key => !activeOrder.includes(key)),
]
